import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL, UserRoles, RoleDisplayNames, formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Trash2, Users, Search, Loader2, Ban, CheckCircle, Unlock } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  usePageTitle('Users');
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // For delete confirmation dialog
  const [suspendDialog, setSuspendDialog] = useState(null); // For suspend confirmation dialog
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  
  // Get allowed roles based on current user's role
  const getAllowedRoles = () => {
    if (currentUser?.role === UserRoles.ADMIN) {
      // Admin can create all roles
      return [
        { value: UserRoles.ADMIN, label: 'Administrator' },
        { value: UserRoles.TRAINING_ADMIN, label: 'Training Admin' },
        { value: UserRoles.INSTRUCTOR, label: 'Instructor' },
        { value: UserRoles.COMPANY, label: 'Company' },
        { value: UserRoles.STUDENT, label: 'Student' }
      ];
    } else if (currentUser?.role === UserRoles.TRAINING_ADMIN) {
      // Training Admin can only create company and student users
      return [
        { value: UserRoles.COMPANY, label: 'Company' },
        { value: UserRoles.STUDENT, label: 'Student' }
      ];
    }
    return [];
  };

  const allowedRoles = getAllowedRoles();
  const defaultRole = allowedRoles.length > 0 ? allowedRoles[allowedRoles.length - 1].value : UserRoles.STUDENT;
  
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    id_number: '',
    role: defaultRole,
    company_id: '',
    person_id: ''
  });

  const fetchData = async () => {
    try {
      const [usersRes, companiesRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/companies?page_size=1000`),
        axios.get(`${API_URL}/persons?page_size=1000`)
      ]);
      setUsers(usersRes.data);
      // Handle paginated responses
      setCompanies(companiesRes.data.items || companiesRes.data);
      setStudents(studentsRes.data.items || studentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      id_number: '',
      role: defaultRole,
      company_id: '',
      person_id: ''
    });
    setStudentSearch('');
  };

  const handleStudentSelect = (student) => {
    setFormData({
      ...formData,
      person_id: student.id,
      email: student.email || '',
      first_name: student.first_name,
      last_name: student.last_name,
      id_number: student.id_number || ''
    });
    setStudentSearch('');
  };

  // Filter students who don't have a user account yet
  const availableStudents = students.filter(s => !s.has_user_account);
  
  const filteredStudents = availableStudents.filter(s =>
    studentSearch && (
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(studentSearch.toLowerCase()) ||
      s.email?.toLowerCase().includes(studentSearch.toLowerCase()) ||
      s.id_number?.toLowerCase().includes(studentSearch.toLowerCase())
    )
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.first_name || !formData.last_name || !formData.id_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    // For student role, person_id is required
    if (formData.role === UserRoles.STUDENT && !formData.person_id) {
      toast.error('Please select a student to link this user account to');
      return;
    }

    setSaving(true);
    
    try {
      const payload = { ...formData };
      if (payload.company_id === '') {
        payload.company_id = null;
      }
      if (payload.person_id === '') {
        payload.person_id = null;
      }
      
      await axios.post(`${API_URL}/users`, payload);
      toast.success('User created successfully', {
        description: `Initial password is: ${formData.id_number}`
      });
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    
    // Show confirmation dialog instead of window.confirm
    setDeleteConfirm(user);
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirm) return;
    
    const userToDelete = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSuspend = (user) => {
    if (user.id === currentUser.id) {
      toast.error('You cannot suspend your own account');
      return;
    }
    setSuspendReason('');
    setSuspendDialog(user);
  };

  const handleUnlock = async (user) => {
    try {
      const res = await axios.post(`${API_URL}/users/${user.id}/unlock`);
      if (res.data?.had_lockout) {
        toast.success(`${user.email} unlocked — they can sign in immediately.`);
      } else {
        toast.success(`${user.email} had no active lockout.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock user');
    }
  };

  const confirmSuspendUser = async () => {
    if (!suspendDialog) return;
    
    const userToSuspend = suspendDialog;
    const shouldSuspend = !userToSuspend.suspended;
    
    setSuspending(true);
    
    try {
      await axios.put(`${API_URL}/users/${userToSuspend.id}/suspend`, {
        suspended: shouldSuspend,
        reason: shouldSuspend ? suspendReason : null
      });
      toast.success(shouldSuspend ? 'User suspended successfully' : 'User unsuspended successfully');
      setSuspendDialog(null);
      setSuspendReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setSuspending(false);
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || '-';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case UserRoles.ADMIN:
        return 'bg-red-100 text-red-800';
      case UserRoles.TRAINING_ADMIN:
        return 'bg-purple-100 text-purple-800';
      case UserRoles.INSTRUCTOR:
        return 'bg-blue-100 text-blue-800';
      case UserRoles.COMPANY:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">
            Manage portal users and their access levels
          </p>
        </div>
        <Button 
          className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            <Select
              value={roleFilter}
              onValueChange={(value) => setRoleFilter(value)}
            >
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="user-role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {Object.values(UserRoles).map((role) => (
                  <SelectItem key={role} value={role}>
                    {RoleDisplayNames[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No users found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm ? 'Try a different search term' : 'Add users to manage portal access'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Role</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Company</TableHead>
                  <TableHead className="font-semibold">Created</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className={`hover:bg-slate-50/50 ${user.suspended ? 'opacity-60' : ''}`} data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">
                      {user.first_name} {user.last_name}
                      {user.must_change_password && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          New
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {RoleDisplayNames[user.role] || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.suspended ? (
                        <Badge className="bg-red-100 text-red-800">
                          <Ban className="w-3 h-3 mr-1" />
                          Suspended
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{getCompanyName(user.company_id)}</TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      {currentUser.role === UserRoles.ADMIN && user.id !== currentUser.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleUnlock(user)}
                              className="text-blue-600"
                              data-testid={`unlock-user-${user.id}`}
                            >
                              <Unlock className="w-4 h-4 mr-2" />
                              Unlock account
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleSuspend(user)}
                              className={user.suspended ? 'text-green-600' : 'text-amber-600'}
                            >
                              {user.suspended ? (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Unsuspend
                                </>
                              ) : (
                                <>
                                  <Ban className="w-4 h-4 mr-2" />
                                  Suspend
                                </>
                              )}
                            </DropdownMenuItem>
                            {currentUser?.role === UserRoles.ADMIN && (
                              <DropdownMenuItem 
                                onClick={() => handleDelete(user)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new portal user. Their initial password will be their ID number.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => {
                  setFormData({ 
                    ...formData, 
                    role: value,
                    person_id: '',
                    // Clear student-specific fields when changing role
                    ...(value !== UserRoles.STUDENT ? { first_name: '', last_name: '', email: '', id_number: '' } : {})
                  });
                  setStudentSearch('');
                }}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowedRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentUser?.role === UserRoles.TRAINING_ADMIN && (
                <p className="text-xs text-slate-500">
                  As Training Admin, you can only create Company and Student users
                </p>
              )}
            </div>

            {formData.role === UserRoles.STUDENT && (
              <div className="space-y-2">
                <Label>Link to Student *</Label>
                {formData.person_id ? (
                  <div className="flex items-center justify-between p-3 border rounded-md bg-slate-50">
                    <div>
                      <p className="font-medium">{formData.first_name} {formData.last_name}</p>
                      <p className="text-sm text-slate-500">{formData.email}</p>
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setFormData({ ...formData, person_id: '', first_name: '', last_name: '', email: '', id_number: '' });
                      }}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search students by name, email, or ID..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      className="pl-10"
                      data-testid="user-student-search"
                    />
                    {filteredStudents.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                        {filteredStudents.map((student) => (
                          <div
                            key={student.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer"
                            onClick={() => handleStudentSelect(student)}
                          >
                            <p className="font-medium">{student.first_name} {student.last_name}</p>
                            <p className="text-xs text-slate-500">
                              {student.email || 'No email'} • ID: {student.id_number || 'N/A'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    {studentSearch && filteredStudents.length === 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg p-3 text-center text-slate-500 text-sm">
                        No students found without user accounts
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Only students without existing user accounts are shown
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  disabled={formData.role === UserRoles.STUDENT && formData.person_id}
                  data-testid="user-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                  disabled={formData.role === UserRoles.STUDENT && formData.person_id}
                  data-testid="user-lastname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john.doe@example.com"
                disabled={formData.role === UserRoles.STUDENT && formData.person_id}
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="id_number">ID Number *</Label>
              <Input
                id="id_number"
                value={formData.id_number}
                onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                placeholder="CY12345678"
                disabled={formData.role === UserRoles.STUDENT && formData.person_id}
                data-testid="user-idnumber-input"
              />
              <p className="text-xs text-slate-500">This will be the user&apos;s initial password</p>
            </div>

            {formData.role === UserRoles.COMPANY && (
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger data-testid="user-company-select">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="user-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete User
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDeleteUser}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Suspend User Confirmation Dialog */}
      {suspendDialog && (
        <Dialog open={true} onOpenChange={() => { setSuspendDialog(null); setSuspendReason(''); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {suspendDialog.suspended ? 'Unsuspend User' : 'Suspend User'}
              </DialogTitle>
              <DialogDescription>
                {suspendDialog.suspended ? (
                  <>
                    Are you sure you want to unsuspend <strong>{suspendDialog.first_name} {suspendDialog.last_name}</strong>? 
                    They will be able to log in again.
                  </>
                ) : (
                  <>
                    Are you sure you want to suspend <strong>{suspendDialog.first_name} {suspendDialog.last_name}</strong>? 
                    They will not be able to log in until unsuspended.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {!suspendDialog.suspended && (
              <div className="space-y-2 py-2">
                <Label htmlFor="suspend_reason">Reason (optional)</Label>
                <Textarea
                  id="suspend_reason"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter reason for suspension..."
                  className="min-h-[80px]"
                />
              </div>
            )}
            
            {suspendDialog.suspended && suspendDialog.suspended_reason && (
              <div className="py-2">
                <p className="text-sm text-slate-500">Previous suspension reason:</p>
                <p className="text-sm text-slate-700 italic mt-1">{suspendDialog.suspended_reason}</p>
              </div>
            )}
            
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => { setSuspendDialog(null); setSuspendReason(''); }}
                disabled={suspending}
              >
                Cancel
              </Button>
              <Button
                className={suspendDialog.suspended 
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-amber-600 hover:bg-amber-700 text-white"
                }
                onClick={confirmSuspendUser}
                disabled={suspending}
              >
                {suspending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {suspendDialog.suspended ? 'Unsuspending...' : 'Suspending...'}
                  </>
                ) : (
                  suspendDialog.suspended ? 'Unsuspend' : 'Suspend'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UserManagement;
